/**
 * A simple promise-based mutual exclusion lock for ensuring sequential execution.
 * Useful for preventing race conditions in async operations.
 */
export class AsyncLock {
    private lock: Promise<void> | null = null;

    /**
     * Executes a function with mutual exclusion.
     * If another execution is in progress, waits for it to complete first.
     *
     * @param fn - The function to execute with the lock held
     * @returns The result of the function
     */
    async withLock<R>(fn: () => R): Promise<R> {
        // Wait for any existing lock to release
        while (this.lock) {
            await this.lock;
        }

        // Acquire lock
        let releaseLock: (() => void) | undefined;
        this.lock = new Promise<void>((resolve) => {
            releaseLock = resolve;
        });

        try {
            return fn();
        } finally {
            // Release lock
            if (releaseLock) {
                releaseLock();
            }
            this.lock = null;
        }
    }
}
